import { useEffect, useState } from "react";

import { useAsyncCurrency, useCurrency } from "../../../Components/hooks/useCurrency";
import { useCurrencyFormat } from "../../../Components/hooks/useCurrencyFormat";

export const useAmountInCustomerCurrency = (amount: number, dontConvert?: boolean) => {
  const { asyncConvertCurrency } = useAsyncCurrency();

  const [convertedAmount, setConvertedAmount] = useState(0);

  useEffect(() => {
    if (!dontConvert) {
      asyncConvertCurrency(amount).then((convertedAmount) => {
        setConvertedAmount(convertedAmount);
      });
    } else {
      setConvertedAmount(amount);
    }
  }, [amount, asyncConvertCurrency, dontConvert]);

  return convertedAmount;
};

export const formatCompactAmount = (amount: number): string => {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return millions >= 10 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
  } else if (amount >= 1_000) {
    const thousands = amount / 1_000;
    return thousands >= 10 ? `${thousands.toFixed(0)}k` : `${thousands.toFixed(1)}k`;
  } else {
    return amount.toFixed(0);
  }
};

export const AmountInCustomerCurrency = ({
  amount,
  dontConvert,
  compactFormat,
}: {
  amount: number;
  dontConvert?: boolean;
  compactFormat?: boolean;
}) => {
  const { customerCurrencySymbol } = useCurrency();
  const formatCurrency = useCurrencyFormat();
  const convertedAmount = useAmountInCustomerCurrency(amount, dontConvert);

  // Format amounts greater than $99,999,999 as "XM+"
  if (convertedAmount > 99_999_999) {
    const millionsAmount = Math.floor(convertedAmount / 1_000_000);
    return `${customerCurrencySymbol}${millionsAmount}M+`;
  }

  // Use compact format if requested
  if (compactFormat) {
    return `${customerCurrencySymbol}${formatCompactAmount(convertedAmount)}`;
  }

  return formatCurrency(convertedAmount);
};

export const useAmountInFormattedCurrency = (amount: number, dontConvert?: boolean) => {
  const formatCurrency = useCurrencyFormat();
  const convertedAmount = useAmountInCustomerCurrency(amount, dontConvert);

  return formatCurrency(convertedAmount);
};
